<div class="flex items-center justify-between gap-3 mb-4">
    <p class="text-sm text-p-text-muted m-0">
        @if (berthName(); as name) {
            {{ 'bookingV2.advanced.power.berthLabel' | translate: { name } }}
        }
    </p>
    @if (!readOnly()) {
        <p-button type="button" (onClick)="toggleForm()">
            <span pButtonIcon class="material-icons" style="font-size: 1.125rem">add</span>
            <span pButtonLabel>{{ 'bookingV2.advanced.power.addButton' | translate }}</span>
        </p-button>
    }
</div>

@if (showForm()) {
    <div class="bg-p-surface-50 border border-p-border rounded-lg p-5 mb-4 flex flex-col gap-4">

        <!-- Berth occupancy (Liegeplatzbelegung) — only when the booking spans several berths -->
        @if (showBerthSelect()) {
            <div class="flex flex-col gap-1">
                <label for="em-berth" class="text-sm font-medium text-p-text">
                    {{ 'bookingV2.advanced.power.berthAssignmentLabel' | translate }}
                </label>
                <p-select
                    inputId="em-berth"
                    styleClass="w-full"
                    appendTo="body"
                    optionValue="id"
                    optionLabel="label"
                    [options]="berthOptions()"
                    [ngModel]="berthId()"
                    (ngModelChange)="berthId.set($event)">
                    <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                </p-select>
            </div>
        }

        <!-- Cabinet (Stromschrank) -->
        <div class="flex flex-col gap-1">
            <label for="em-cabinet" class="text-sm font-medium text-p-text">
                {{ 'bookingV2.advanced.power.cabinetLabel' | translate }}
                <span class="text-red-500" aria-hidden="true">*</span>
            </label>
            @if (loadingCabinets()) {
                <div class="flex items-center gap-2 text-sm text-p-text-muted py-2" role="status" aria-live="polite">
                    <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                    {{ 'bookingV2.advanced.power.cabinetLoading' | translate }}
                </div>
            } @else {
                <p-select
                    inputId="em-cabinet"
                    styleClass="w-full"
                    appendTo="body"
                    optionValue="id"
                    optionLabel="label"
                    optionDisabled="disabled"
                    [options]="cabinets()"
                    [ngModel]="cabinetId()"
                    (ngModelChange)="onCabinetChange($event)"
                    [placeholder]="('bookingV2.advanced.power.cabinetPlaceholder' | translate)">
                    <ng-template let-opt #item>
                        <div class="flex flex-col">
                            <span>{{ opt.label }}</span>
                            @if (opt.disabled) {
                                <small class="text-xs text-p-text-muted">{{ 'bookingV2.advanced.power.cabinetPhysicalOnly' | translate }}</small>
                            }
                        </div>
                    </ng-template>
                    <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                </p-select>
            }
        </div>

        <!-- Meter (Zähler) -->
        <div class="flex flex-col gap-1">
            <label for="em-meter" class="text-sm font-medium text-p-text">
                {{ 'bookingV2.advanced.power.meterLabel' | translate }}
                <span class="text-red-500" aria-hidden="true">*</span>
            </label>
            @if (cabinetId() === null) {
                <p class="text-sm text-p-text-muted m-0 py-2">{{ 'bookingV2.advanced.power.meterSelectCabinetFirst' | translate }}</p>
            } @else if (loadingMeters()) {
                <div class="flex items-center gap-2 text-sm text-p-text-muted py-2" role="status" aria-live="polite">
                    <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                    {{ 'bookingV2.advanced.power.meterLoading' | translate }}
                </div>
            } @else if (availableMeters().length === 0) {
                <p class="text-sm text-p-text-muted m-0 py-2">{{ 'bookingV2.advanced.power.meterNone' | translate }}</p>
            } @else {
                <p-select
                    inputId="em-meter"
                    styleClass="w-full"
                    appendTo="body"
                    optionValue="id"
                    optionLabel="fullName"
                    optionDisabled="disabled"
                    [options]="availableMeters()"
                    [ngModel]="meterId()"
                    (ngModelChange)="onMeterChange($event)"
                    [placeholder]="('bookingV2.advanced.power.meterPlaceholder' | translate)">
                    <ng-template let-opt #item>
                        <div class="flex items-center gap-2 w-full">
                            <span class="flex-1 min-w-0 truncate">{{ opt.fullName }}</span>
                            @if (opt.fuse) {
                                <span class="text-xs bg-p-surface-100 text-p-text-muted px-1.5 py-0.5 rounded">{{ opt.fuse }}</span>
                            }
                            @if (opt.occupied) {
                                <span class="text-xs bg-p-surface-100 text-p-text-muted px-1.5 py-0.5 rounded">
                                    {{ 'bookingV2.advanced.power.occupied' | translate }}
                                </span>
                            }
                            @if (opt.hasOpenReading) {
                                <span class="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                    {{ 'bookingV2.advanced.power.openReading' | translate }}
                                </span>
                            }
                        </div>
                    </ng-template>
                    <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                </p-select>
            }
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <!-- Connection date (Anschlussdatum) -->
            <div class="flex flex-col gap-1">
                <label for="em-from" class="text-sm font-medium text-p-text">
                    {{ 'bookingV2.advanced.power.fromLabel' | translate }}
                    <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p-datepicker
                    inputId="em-from"
                    u2bDateMask
                    [u2bDateMaskTime]="true"
                    u2bDatePickerApply
                    #applyFrom="u2bDatePickerApply"
                    [pt]="customTimePt"
                    dateFormat="dd.mm.yy"
                    [showTime]="true"
                    hourFormat="24"
                    [showIcon]="true"
                    appendTo="body"
                    styleClass="w-full"
                    [maxDate]="today"
                    [placeholder]="('bookingV2.advanced.power.dateTimePlaceholder' | translate)"
                    [ngModel]="from()"
                    (ngModelChange)="onFromChange($event)">
                    <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                    <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                    <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                    <ng-template #buttonbar>
                        <u2b-date-picker-button-bar [picker]="applyFrom"/>
                    </ng-template>
                </p-datepicker>
                @if (fromIsFuture()) {
                    <small class="text-xs text-red-600">{{ 'bookingV2.advanced.power.noFutureDate' | translate }}</small>
                }
            </div>

            <!-- Initial meter reading (Anfangszählerstand) -->
            <div class="flex flex-col gap-1">
                <label for="em-reading" class="text-sm font-medium text-p-text">
                    {{ 'bookingV2.advanced.power.meterReadingStartLabel' | translate }}
                    @if (!isApiMeter()) {
                        <span class="text-red-500" aria-hidden="true">*</span>
                    }
                </label>
                @if (isApiMeter()) {
                    <!-- Provider-controlled (CampConcept/ZFA): read live, no manual entry. -->
                    @if (liveReadingLoading()) {
                        <div class="flex items-center gap-2 text-sm text-p-text-muted py-2" role="status" aria-live="polite">
                            <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                            {{ 'bookingV2.advanced.power.liveReadingLoading' | translate }}
                        </div>
                    } @else if (liveReading() !== null) {
                        <div class="flex items-center gap-2 py-2">
                            <span class="font-semibold text-p-text">{{ liveReading() | number:'1.0-2' }} kWh</span>
                            <span class="text-xs text-p-text-muted">{{ 'bookingV2.advanced.power.liveReadingHint' | translate }}</span>
                        </div>
                    } @else {
                        <p class="text-sm text-p-text-muted m-0 py-2">{{ 'bookingV2.advanced.power.liveReadingUnavailable' | translate }}</p>
                    }
                } @else {
                    <p-inputnumber
                        inputId="em-reading"
                        styleClass="w-full"
                        mode="decimal"
                        [minFractionDigits]="0"
                        [maxFractionDigits]="3"
                        [locale]="currentLocale"
                        [min]="selectedMeter()?.lastReading ?? 0"
                        [disabled]="meterId() === null"
                        [ngModel]="meterReadingStart()"
                        (ngModelChange)="meterReadingStart.set($event)" />
                }
            </div>
        </div>

        <!-- Billing type (Abrechnungsart) -->
        <div class="flex flex-col gap-1">
            <label for="em-pt" class="text-sm font-medium text-p-text">
                {{ 'bookingV2.advanced.power.productTypeLabel' | translate }}
                <span class="text-red-500" aria-hidden="true">*</span>
            </label>
            <p-select
                inputId="em-pt"
                styleClass="w-full"
                appendTo="body"
                optionValue="id"
                optionLabel="description"
                [options]="productTypes()"
                [ngModel]="productTypeId()"
                (ngModelChange)="onProductTypeChange($event)"
                [placeholder]="('bookingV2.advanced.power.productTypePlaceholder' | translate)">
                <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
            </p-select>
        </div>

        <!-- Electricity product (Stromprodukt) + amount -->
        @if (showProduct()) {
            <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-1">
                    <label for="em-product" class="text-sm font-medium text-p-text">
                        {{ 'bookingV2.advanced.power.productLabel' | translate }}
                        <span class="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <p-select
                        inputId="em-product"
                        styleClass="w-full"
                        appendTo="body"
                        [filter]="true"
                        filterBy="name"
                        optionValue="id"
                        optionLabel="label"
                        [options]="electricityProducts()"
                        [ngModel]="productId()"
                        (ngModelChange)="productId.set($event)"
                        [placeholder]="('bookingV2.advanced.power.productPlaceholder' | translate)">
                        <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                        <ng-template #filtericon><span class="material-icons">search</span></ng-template>
                    </p-select>
                    <small class="text-xs text-p-text-muted">{{ 'bookingV2.advanced.power.productHint' | translate }}</small>
                </div>

                @if (showQuantity()) {
                    <div class="flex flex-col gap-1">
                        <label for="em-quantity" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.advanced.power.quantityLabel' | translate }}
                            <span class="text-red-500" aria-hidden="true">*</span>
                        </label>
                        <p-inputnumber
                            inputId="em-quantity"
                            styleClass="w-full"
                            mode="decimal"
                            [minFractionDigits]="0"
                            [maxFractionDigits]="3"
                            [locale]="currentLocale"
                            [min]="0"
                            [ngModel]="quantity()"
                            (ngModelChange)="quantity.set($event)" />
                    </div>
                }
            </div>
        }

        <!-- Additional flat base fee (Grundgebühr) — only for by-usage billing -->
        @if (showAdditionalFee()) {
            <div class="flex flex-col gap-3">
                <label class="flex items-center gap-2 text-sm font-medium text-p-text cursor-pointer">
                    <p-checkbox
                        inputId="em-addfee"
                        [binary]="true"
                        [ngModel]="additionalFeeEnabled()"
                        (ngModelChange)="additionalFeeEnabled.set($event)" />
                    {{ 'bookingV2.advanced.power.additionalFeeToggle' | translate }}
                </label>

                @if (additionalFeeEnabled()) {
                    <div class="flex flex-col gap-1">
                        <label for="em-addfee-product" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.advanced.power.additionalFeeProductLabel' | translate }}
                            <span class="text-red-500" aria-hidden="true">*</span>
                        </label>
                        <p-select
                            inputId="em-addfee-product"
                            styleClass="w-full"
                            appendTo="body"
                            [filter]="true"
                            filterBy="name"
                            optionValue="id"
                            optionLabel="label"
                            [options]="electricityProducts()"
                            [ngModel]="additionalProductId()"
                            (ngModelChange)="additionalProductId.set($event)"
                            [placeholder]="('bookingV2.advanced.power.productPlaceholder' | translate)">
                            <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                            <ng-template #filtericon><span class="material-icons">search</span></ng-template>
                        </p-select>
                    </div>

                    <div class="flex flex-col gap-1">
                        <label for="em-addfee-qty" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.advanced.power.quantityLabel' | translate }}
                        </label>
                        <p-inputnumber
                            inputId="em-addfee-qty"
                            styleClass="w-full"
                            mode="decimal"
                            [minFractionDigits]="0"
                            [maxFractionDigits]="2"
                            [locale]="currentLocale"
                            [min]="0"
                            [ngModel]="additionalQuantity()"
                            (ngModelChange)="additionalQuantity.set($event)" />
                    </div>
                }
            </div>
        }

        <div class="flex gap-2 justify-end">
            <p-button type="button" [disabled]="!canSave()" [label]="'common.save' | translate" (onClick)="save()" />
            <p-button type="button" severity="secondary" [text]="true" [label]="'common.cancel' | translate" (onClick)="closeForm()" />
        </div>
    </div>
}

<div class="flex flex-col gap-2">
    @for (assignment of assignments(); track assignment.uuid) {
        <u2b-indicator-card accent="amber" icon="bolt">
            <div class="min-w-0">
                <p class="font-semibold text-p-text m-0">
                    {{ assignment.meterFullName }}
                    @if (showDualUse && assignment.hasDualUse) {
                        <span class="ml-1 align-middle text-xs font-medium px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700">
                            {{ 'bookingV2.advanced.power.dualUseBadge' | translate }}
                        </span>
                    }
                </p>

                @if (assignment.reparent) {
                    <p class="text-sm font-medium text-amber-600 m-0 mt-1">
                        {{ 'bookingV2.advanced.power.proposedBadge' | translate }}
                    </p>
                    <p class="text-xs text-p-text-muted m-0">
                        {{ 'bookingV2.advanced.power.proposedHint' | translate }}
                    </p>
                }

                    <div class="mt-1 flex flex-col gap-0.5">
                        @if (showBerthSelect() && assignment.berthLabel) {
                            <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.advanced.power.berthAssignmentLabel' | translate }}: <span class="text-p-text">{{ assignment.berthLabel }}</span></p>
                        }
                        @if (assignment.cabinetHandle) {
                            <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.advanced.power.cabinetLabel' | translate }}: <span class="text-p-text">{{ assignment.cabinetHandle }}</span></p>
                        }
                        @if (assignment.from) {
                            <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.advanced.power.fromLabel' | translate }}: <span class="text-p-text">{{ assignment.from | date:'dd.MM.yyyy HH:mm' }}</span></p>
                        }
                        <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.advanced.power.productTypeLabel' | translate }}: <span class="text-p-text">{{ assignment.productTypeLabel }}</span></p>
                        @if (assignment.productName) {
                            <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.advanced.power.productLabel' | translate }}: <span class="text-p-text">{{ assignment.productName }}</span></p>
                        }
                        @if (assignment.quantity !== null) {
                            <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.advanced.power.quantityLabel' | translate }}: <span class="text-p-text">{{ fmtQty(assignment.quantity) }}</span></p>
                        }
                        @if (assignment.additionalFee) {
                            <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.advanced.power.additionalFeeTitle' | translate }}: <span class="text-p-text">×{{ fmtQty(assignment.additionalFee.quantity) }}</span></p>
                        }
                        <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.advanced.power.readingPrefix' | translate }}: <span class="text-p-text">{{ assignment.meterReadingStart | number:'1.0-2' }} kWh</span></p>

                        @if (assignment.meterReadingEnd !== null) {
                            <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.advanced.power.readingEndLabel' | translate }}: <span class="text-p-text">{{ assignment.meterReadingEnd | number:'1.0-2' }} kWh</span></p>
                        }

                        @if (assignment.provider?.switchableViaWeb && assignment.meterReadingEnd === null && cardLiveReadings()[assignment.uuid] != null) {
                            <p class="text-sm text-p-text-muted m-0">
                                {{ 'bookingV2.advanced.power.currentReadingLabel' | translate }}:
                                <span class="font-semibold text-p-text">{{ cardLiveReadings()[assignment.uuid] | number:'1.0-2' }} kWh</span>
                                <span class="text-xs">{{ 'bookingV2.advanced.power.liveReadingHint' | translate }}</span>
                            </p>
                        }

                        @if (consumption(assignment) !== null) {
                            <p class="text-sm text-p-text-muted m-0">{{ 'dialogs.booking.electricAssignment.consumptionSinceOn' | translate }}: <span class="text-p-text">{{ consumption(assignment) | number:'1.0-2' }} kWh</span></p>
                        }
                        @if (liveConsumption(assignment) !== null && assignment.meterReadingEnd === null) {
                            <p class="text-sm text-p-text-muted m-0">{{ 'dialogs.booking.electricAssignment.consumptionSinceOn' | translate }} ({{ 'bookingV2.advanced.power.liveReadingHint' | translate }}): <span class="text-p-text">{{ liveConsumption(assignment) | number:'1.0-2' }} kWh</span></p>
                        }
                    </div>

                    @if (assignment.disconnected) {
                        <p class="text-sm font-medium text-amber-600 m-0 mt-1">
                            {{ 'bookingV2.advanced.power.disconnectStaged' | translate }}
                        </p>
                    } @else if (assignment.to !== null) {
                        <p class="text-sm font-medium text-p-text-muted m-0 mt-1">
                            {{ 'bookingV2.advanced.power.disconnectedOn' | translate: { date: (assignment.to | date:'dd.MM.yyyy HH:mm') } }}
                        </p>
                    }
                </div>
            @if (!readOnly()) {
                <div u2bIndicatorAction class="flex items-center gap-1 flex-shrink-0">
                    @if (assignment.reparent) {
                        <!-- Proposed take-over: the only action is to NOT take it
                             over (drop the card). Per-meter actions (interim/
                             disconnect) stay hidden until the booking is saved. -->
                        <u2b-icon-button
                            icon="close"
                            [tooltip]="('bookingV2.advanced.power.proposedRemoveAria' | translate)"
                            [ariaLabel]="('bookingV2.advanced.power.proposedRemoveAria' | translate)"
                            (clicked)="remove(assignment.uuid)" />
                    } @else if (assignment.disconnected) {
                        <!-- Disconnect staged for commit — allow undo. -->
                        <u2b-icon-button
                            icon="undo"
                            [tooltip]="('bookingV2.advanced.power.action.undoDisconnect' | translate)"
                            [ariaLabel]="('bookingV2.advanced.power.action.undoDisconnect' | translate)"
                            (clicked)="undoDisconnect(assignment.uuid)" />
                    } @else if (assignment.to === null) {
                        <!-- Open connection: actions live in a three-dots overflow menu.
                             Interim/disconnect need a persisted assignment, so they are
                             disabled until the booking is saved; editing the start
                             reading and removing work on the local draft too. -->
                        <u2b-icon-button
                            icon="more_vert"
                            [ariaLabel]="('bookingV2.advanced.power.action.menuAria' | translate)"
                            (clicked)="emMenu.toggle($event)" />
                        <p-popover #emMenu appendTo="body">
                            <div class="flex flex-col gap-0.5 p-1 min-w-[14rem]">
                                <button type="button"
                                        class="flex items-center gap-3 px-3 py-2 w-full text-sm text-left text-p-text bg-transparent border-0 appearance-none cursor-pointer rounded-md transition-colors hover:bg-[var(--p-primary-50)] hover:text-p-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-p-text"
                                        [disabled]="isPhysicalOnly(assignment) || assignment.assignmentId == null"
                                        [pTooltip]="isPhysicalOnly(assignment) ? ('bookingV2.advanced.power.action.providerNoWebSwitch' | translate) : (assignment.assignmentId == null ? ('bookingV2.advanced.power.action.saveBookingFirst' | translate) : null)"
                                        tooltipPosition="left"
                                        (click)="startAction('intermediate', assignment); emMenu.hide()">
                                    <span class="material-icons text-base">swap_horiz</span>
                                    {{ 'bookingV2.advanced.power.action.intermediate' | translate }}
                                </button>
                                <button type="button"
                                        class="flex items-center gap-3 px-3 py-2 w-full text-sm text-left text-p-text bg-transparent border-0 appearance-none cursor-pointer rounded-md transition-colors hover:bg-[var(--p-primary-50)] hover:text-p-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-p-text"
                                        [disabled]="isPhysicalOnly(assignment) || assignment.assignmentId == null"
                                        [pTooltip]="isPhysicalOnly(assignment) ? ('bookingV2.advanced.power.action.providerNoWebSwitch' | translate) : (assignment.assignmentId == null ? ('bookingV2.advanced.power.action.saveBookingFirst' | translate) : null)"
                                        tooltipPosition="left"
                                        (click)="startAction('disconnect', assignment); emMenu.hide()">
                                    <span class="material-icons text-base">link_off</span>
                                    {{ 'bookingV2.advanced.power.action.disconnect' | translate }}
                                </button>
                                @if (assignment.readingId != null || assignment.assignmentId == null) {
                                    <button type="button"
                                            class="flex items-center gap-3 px-3 py-2 w-full text-sm text-left text-p-text bg-transparent border-0 appearance-none cursor-pointer rounded-md transition-colors hover:bg-[var(--p-primary-50)] hover:text-p-primary"
                                            (click)="startAction('editStart', assignment); emMenu.hide()">
                                        <span class="material-icons text-base">edit_calendar</span>
                                        {{ 'bookingV2.advanced.power.action.editStart' | translate }}
                                    </button>
                                }
                                @if (assignment.assignmentId == null || assignment.to === null) {
                                    <button type="button"
                                            class="flex items-center gap-3 px-3 py-2 w-full text-sm text-left text-red-600 bg-transparent border-0 appearance-none cursor-pointer rounded-md transition-colors hover:bg-[var(--p-primary-50)]"
                                            (click)="remove(assignment.uuid); emMenu.hide()">
                                        <span class="material-icons text-base">close</span>
                                        {{ 'bookingV2.advanced.power.action.removeReading' | translate }}
                                    </button>
                                }
                            </div>
                        </p-popover>
                    }
                </div>
            }
        </u2b-indicator-card>
    }
    @if (assignments().length === 0 && !showForm()) {
        <p class="text-center text-p-text-muted py-6 text-sm m-0">
            {{ 'bookingV2.advanced.power.empty' | translate }}
        </p>
    }
</div>

@if (activeAction(); as act) {
    <booking-v2-electric-meter-reading-dialog
        [mode]="act.mode"
        [assignment]="act.assignment"
        [liveReading]="act.liveReading"
        [products]="electricityProducts()"
        [visible]="true"
        (visibleChange)="onActionVisibleChange($event)"
        (confirmed)="onActionConfirmed($event)" />
}
